import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { ProgressBarCampaign } from './shared/ProgressBarCampaign';
import { CampaignOverviewCard } from './shared/CampaignOverviewCard';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StepperRouting } from './shared/StepperRouting';
import { ICampaignAudience } from '../../../../../model/briefing/step6/ICampaignAudience';
import { useNavigate, useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import { BriefingService } from '../service';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { oneStepUp } from '../../../../../shared/functions/Functions';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import CardLayout from '../../../../../shared/components/card/CardLayout';

export const StepSix = () => {
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>();
    const navigate = useNavigate();

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [currentStep, setCurrentStep] = useState<ICampaignAudience>();
    const [isNextClicked, setIsNextClicked] = useState(false);
    const [openWarningModal, setOpenWarningModal] = useState(false);
    const { gender, country } = useSelector((states: IAllStates) => states.enums);
    const [isBriefingComplete, setIsBriefingComplete] = useState(true);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value, name } = event.target;
        setCurrentStep((prev: any) => ({ ...prev, [name]: +value ? +value : value }));
    };

    const multipleSelectHandler = (paramKey: string, paramValue: any) => {
        const isArray = paramKey === 'audienceCountriesArray';
        setCurrentStep((prev: any) => ({
            ...prev,
            [paramKey]: isArray ? paramValue : paramValue === '' ? null : paramValue,
        }));
    };

    useEffect(() => {
        !!+id! &&
            BriefingService.getBriefingByStep(id!, 'sixth')
                .then(response => {
                    setCurrentStep(response);
                    setIsBriefingComplete(response?.isBriefingComplete);
                })
                .catch(error => ErrorToast(error));
    }, [id]);

    const submitHandler = (openedFromModal: boolean) => {
        setIsSubmitted(true);
        const hasError = !currentStep?.audienceCountriesArray.length;
        const hasAudienceData =
            !currentStep?.audienceAgeMin || !currentStep?.audienceAgeMax || !currentStep?.audienceGender;
        if (hasError) {
            return;
        }
        if (hasAudienceData && !openedFromModal) {
            setOpenWarningModal(true);
            return;
        }
        setIsNextClicked(true);
        id &&
            BriefingService.updateBriefingByStep(
                id!,
                {
                    ...currentStep,
                    audienceCountries: currentStep?.audienceCountriesArray,
                },
                'sixth'
            )
                .then(response => {
                    if (response) {
                        oneStepUp(navigate, id ?? '', response.isBriefingComplete, response.briefingStep);
                        SuccessToast(`${t('general.alert.success_updated_campaign_step')}`);
                    }
                })
                .catch(error => ErrorToast(error))
                .finally(() => {
                    setIsNextClicked(false);
                });
    };

    return (
        <>
            <ConfirmationModal
                title={t('general.confirm')}
                description={t('brand.campaign.briefing.step2.areYouSure')}
                action={() => submitHandler(true)}
                show={openWarningModal}
                closeModal={() => setOpenWarningModal(false)}
            />
            <Row className="mb-5 mb-md-0">
                <Col md={8} className="mb-sm-0 mb-2">
                    {!isBriefingComplete && (
                        <div className="mb-4">
                            <ProgressBarCampaign title={t('brand.campaign.briefing.step6.audience')} percent={60} />
                        </div>
                    )}

                    <CardLayout>
                        <div className="d-flex align-items-md-end flex-column flex-md-row">
                            <div className="w-100">
                                <h6>{t('brand.campaign.briefing.step6.describe_audience')}</h6>
                                <Form.Control
                                    as="textarea"
                                    onChange={changeHandler}
                                    name="audienceDesc"
                                    className={`${isSubmitted && !currentStep?.audienceDesc ? 'border-danger' : ''}`}
                                    value={currentStep?.audienceDesc}
                                    maxLength={2000}
                                    placeholder="Audience Description (2000 characters)"
                                    rows={7}
                                />
                            </div>
                            {/*<div className="w-md-50 ml-md-2 mt-3 mt-md-0">*/}
                            {/* <InfoBox className="mb-0"
                                                    variant="info"
                                                    title="influence.vision Feedback"
                                                    text="This Campaign for cocaine is allowed in Austria. This Campaign for cocaine is allowed in Austria.
                                        This Campaign for cocaine is allowed in Austria. This Campaign for cocaine is allowed in Austria."
                                                    buttonName=""
                                        /> */}
                            {/*</div>*/}
                        </div>
                        <div className="d-flex align-items-md-center mt-4 flex-column flex-md-row">
                            <div className="d-flex flex-column">
                                <h6>{t('brand.campaign.briefing.step6.gender_preference')}</h6>
                                <div>
                                    {currentStep && (
                                        <CustomAutocomplete
                                            dataArray={gender}
                                            isMultiple={false}
                                            defaultArray={currentStep?.audienceGender}
                                            isWithImage={false}
                                            inputLabel="gender"
                                            queryFilterKey="audienceGender"
                                            inputLabelTranslation="Gender"
                                            handleChangeParams={multipleSelectHandler}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="d-flex flex-column ml-md-3 mt-3 mt-md-0">
                                <h6>{t('brand.campaign.briefing.step5.age_range')}</h6>
                                <div className="d-flex align-items-center">
                                    <TextField
                                        label={`${t('brand.campaign.book.offer.filters.min')}`}
                                        variant="outlined"
                                        size="small"
                                        focused={!!currentStep?.audienceAgeMin}
                                        name="audienceAgeMin"
                                        type="number"
                                        value={currentStep?.audienceAgeMin}
                                        onChange={changeHandler}
                                        InputProps={{ inputProps: { min: 0 } }}
                                        onKeyPress={event =>
                                            (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                        }
                                    />
                                    <p className="mx-2 mb-0">-</p>
                                    <TextField
                                        label={`${t('brand.campaign.book.offer.filters.max')}`}
                                        variant="outlined"
                                        size="small"
                                        name="audienceAgeMax"
                                        focused={!!currentStep?.audienceAgeMax}
                                        type="number"
                                        onChange={changeHandler}
                                        value={currentStep?.audienceAgeMax}
                                        InputProps={{ inputProps: { min: 0 } }}
                                        onKeyPress={event =>
                                            (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center my-3 flex-column flex-md-row">
                            <div className="d-flex flex-column w-md-50 mr-md-3">
                                <h6>{t('brand.campaign.briefing.step6.audience_location')}</h6>
                                {currentStep && (
                                    <CustomAutocomplete
                                        dataArray={country}
                                        isWithImage={true}
                                        inputLabel="country"
                                        defaultArray={currentStep?.audienceCountriesArray}
                                        isMultiple={true}
                                        hasError={isSubmitted && !currentStep?.audienceCountriesArray?.length}
                                        photoLabel="countryFlag"
                                        queryFilterKey="audienceCountriesArray"
                                        inputLabelTranslation={`${t('brand.campaign.book.offer.filters.location')}`}
                                        handleChangeParams={multipleSelectHandler}
                                    />
                                )}
                            </div>
                            {/*<div className="d-flex flex-column w-md-25 mt-3 mt-md-0">*/}
                            {/*    <h6>*/}
                            {/*        Min Audience*/}
                            {/*    </h6>*/}
                            {/*    <TextField*/}
                            {/*        label="%"*/}
                            {/*        variant="outlined"*/}
                            {/*        size="small"*/}
                            {/*        name="audienceMin"*/}
                            {/*        type="number"*/}
                            {/*        InputProps={{inputProps: {min: 0}}}*/}
                            {/*        onKeyPress={(event) => (event?.key === '-' || event?.key === '+') && event.preventDefault()}*/}
                            {/*    />*/}
                            {/*</div>*/}
                        </div>
                        <StepperRouting
                            submitStepHandler={() => submitHandler(false)}
                            isNextDisabled={isNextClicked}
                            status={currentStep?.status}
                        />
                    </CardLayout>
                </Col>

                <Col md={4} className="pr-md-4">
                    <CampaignOverviewCard />
                </Col>
            </Row>
        </>
    );
};
